if (!customElements.get('collection-quick-order')) {
  customElements.define(
    'collection-quick-order',
    class CollectionQuickOrder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.cqoReady === 'true') return;

        this.dataset.cqoReady = 'true';
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('change', this.onChange.bind(this));

        this.querySelectorAll('[data-cqo-product]').forEach((product) => this.updateProduct(product));
      }

      onClick(event) {
        const increaseButton = event.target.closest('[data-cqo-increase]');
        const decreaseButton = event.target.closest('[data-cqo-decrease]');
        const addButton = event.target.closest('[data-cqo-add-product]');

        if (increaseButton && this.contains(increaseButton)) {
          event.preventDefault();
          this.adjustQuantity(increaseButton, 1);
          return;
        }

        if (decreaseButton && this.contains(decreaseButton)) {
          event.preventDefault();
          this.adjustQuantity(decreaseButton, -1);
          return;
        }

        if (addButton && this.contains(addButton)) {
          event.preventDefault();
          this.addProductSelection(addButton.closest('[data-cqo-product]'));
        }
      }

      onInput(event) {
        if (!event.target.matches('[data-cqo-quantity]')) return;

        const product = event.target.closest('[data-cqo-product]');
        this.clearStatus(product);
        this.updateProduct(product);
      }

      onChange(event) {
        if (!event.target.matches('[data-cqo-quantity]')) return;

        event.target.value = this.normalizedQuantity(event.target);
        this.updateProduct(event.target.closest('[data-cqo-product]'));
      }

      adjustQuantity(button, direction) {
        const row = button.closest('[data-cqo-row]');
        const input = row?.querySelector('[data-cqo-quantity]');
        if (!input || input.disabled) return;

        const current = this.numberValue(input.value);
        const minimum = this.numberValue(input.dataset.minOrder) || 1;
        const increment = this.numberValue(input.dataset.increment) || 1;
        const maximum = this.numberValue(input.dataset.maxOrder) || Infinity;
        let next;

        if (direction > 0) {
          next = current === 0 ? minimum : current + increment;
          next = Math.min(next, maximum);
        } else {
          next = current <= minimum ? 0 : Math.max(minimum, current - increment);
        }

        input.value = next;
        const product = row.closest('[data-cqo-product]');
        this.clearStatus(product);
        this.updateProduct(product);
      }

      normalizedQuantity(input) {
        const raw = Math.max(0, this.numberValue(input.value));
        if (raw === 0) return 0;

        const minimum = this.numberValue(input.dataset.minOrder) || 1;
        const increment = this.numberValue(input.dataset.increment) || 1;
        const maximum = this.numberValue(input.dataset.maxOrder) || Infinity;
        const stepped = minimum + Math.round((Math.max(raw, minimum) - minimum) / increment) * increment;

        return Math.min(stepped, maximum);
      }

      numberValue(value) {
        const number = Number.parseInt(value, 10);
        return Number.isFinite(number) ? number : 0;
      }

      selectionFor(product) {
        return Array.from(product.querySelectorAll('[data-cqo-row]'))
          .map((row) => {
            const input = row.querySelector('[data-cqo-quantity]');
            return {
              id: Number(row.dataset.variantId),
              quantity: input ? this.normalizedQuantity(input) : 0,
            };
          })
          .filter((item) => item.id && item.quantity > 0);
      }

      updateProduct(product) {
        if (!product) return;

        const items = this.selectionFor(product);
        const flavorCount = items.length;
        const unitCount = items.reduce((total, item) => total + item.quantity, 0);
        const summary = product.querySelector('[data-cqo-summary]');
        const addButton = product.querySelector('[data-cqo-add-product]');
        const buttonText = addButton?.querySelector('[data-cqo-button-text]');

        if (summary) {
          summary.textContent = unitCount
            ? `${flavorCount} ${flavorCount === 1 ? 'flavor' : 'flavors'} selected • ${unitCount} ${unitCount === 1 ? 'item' : 'items'}`
            : 'Choose quantities above';
        }

        if (addButton) {
          addButton.disabled = unitCount === 0 || product.classList.contains('is-busy');
          if (buttonText) {
            const defaultLabel = addButton.dataset.defaultLabel || 'Add selected to cart';
            buttonText.textContent = unitCount ? `${defaultLabel} (${unitCount})` : defaultLabel;
          }
        }
      }

      async addProductSelection(product) {
        if (!product || product.classList.contains('is-busy')) return;

        const items = this.selectionFor(product);
        if (!items.length) return;

        const flavorCount = items.length;
        const unitCount = items.reduce((total, item) => total + item.quantity, 0);
        const status = product.querySelector('[data-cqo-status]');

        this.setBusy(product, true);
        this.setStatus(status, 'Adding selected flavors…');

        try {
          const root = window.Shopify?.routes?.root || '/';
          const addResponse = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          const addResult = await this.parseResponse(addResponse);
          if (!addResponse.ok) {
            throw new Error(addResult.description || addResult.message || 'The selected flavors could not be added.');
          }

          try {
            const cartResponse = await fetch(`${root}cart.js`, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            const cart = await this.parseResponse(cartResponse);
            if (cartResponse.ok) this.updateCartCount(cart);
          } catch (_cartError) {
            // The cart add already succeeded. A count-refresh failure must not invite a duplicate retry.
          }

          product.querySelectorAll('[data-cqo-quantity]').forEach((input) => {
            input.value = 0;
          });

          this.setStatus(
            status,
            `Added ${unitCount} ${unitCount === 1 ? 'item' : 'items'} across ${flavorCount} ${flavorCount === 1 ? 'flavor' : 'flavors'}. Your cart stayed closed.`,
            'success'
          );
        } catch (error) {
          this.setStatus(status, error.message || 'Something went wrong. Please try again.', 'error');
        } finally {
          this.setBusy(product, false);
          this.updateProduct(product);
        }
      }

      async parseResponse(response) {
        try {
          return await response.json();
        } catch (_error) {
          return {};
        }
      }

      setBusy(product, busy) {
        product.classList.toggle('is-busy', busy);

        product.querySelectorAll('[data-cqo-increase], [data-cqo-decrease], [data-cqo-quantity]').forEach((control) => {
          if (busy) {
            control.dataset.cqoWasDisabled = control.disabled ? 'true' : 'false';
            control.disabled = true;
          } else if (control.dataset.cqoWasDisabled === 'false') {
            control.disabled = false;
            delete control.dataset.cqoWasDisabled;
          }
        });

        const addButton = product.querySelector('[data-cqo-add-product]');
        addButton?.classList.toggle('is-loading', busy);
        if (addButton) addButton.disabled = true;
      }

      setStatus(element, message, state) {
        if (!element) return;

        element.textContent = message;
        element.classList.toggle('is-success', state === 'success');
        element.classList.toggle('is-error', state === 'error');
      }

      clearStatus(product) {
        const status = product?.querySelector('[data-cqo-status]');
        if (status?.textContent) this.setStatus(status, '');
      }

      updateCartCount(cart) {
        const itemCount = this.numberValue(cart.item_count);
        const displayCount = itemCount < 100 ? String(itemCount) : '99+';

        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          const usesParentheses = Boolean(element.closest('.cart-text')) || element.id === 'ggd-cart-count';
          element.textContent = usesParentheses ? `(${displayCount})` : displayCount;
        });

        document.querySelectorAll('#ggd-cart-count').forEach((element) => {
          element.textContent = `(${displayCount})`;
        });

        document.querySelectorAll('[data-cart-text]').forEach((element) => {
          element.textContent = `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
        });

        if (itemCount > 0) {
          document.querySelectorAll('#cart-icon-bubble.header__icon--cart').forEach((cartIcon) => {
            if (cartIcon.closest('noscript') || cartIcon.querySelector('.cart-count-bubble')) return;

            const bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            const visibleCount = document.createElement('span');
            visibleCount.setAttribute('aria-hidden', 'true');
            visibleCount.setAttribute('data-cart-count', '');
            visibleCount.textContent = displayCount;
            const accessibleCount = document.createElement('span');
            accessibleCount.className = 'visually-hidden';
            accessibleCount.setAttribute('data-cart-text', '');
            accessibleCount.textContent = `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
            bubble.append(visibleCount, accessibleCount);
            cartIcon.append(bubble);
          });
        }

        document.querySelectorAll('.cart-count-bubble, .cart-icon-bubble').forEach((bubble) => {
          bubble.classList.toggle('hidden', itemCount === 0);
        });

        document.dispatchEvent(
          new CustomEvent('collection-quick-order:cart-updated', {
            detail: { cart },
          })
        );
      }
    }
  );
}
